import { jwtDecode } from 'jwt-decode';

const BUCKET_STORAGE_KEY = 'welya.live-api-bucket';
const BUCKET_COUNT = 100;

type BucketStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function parseCanaryPercent(raw: string | undefined): number {
  const percent = Number(raw);
  if (!Number.isFinite(percent)) return 0;
  return Math.min(Math.max(percent, 0), 100);
}

export function browserCanaryStorage(): BucketStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Tirage 0..99 propre au navigateur : un spectateur reste du même côté du canary d'une visite à l'autre. */
export function readCanaryBucket(
  storage: BucketStorage | null,
  random: () => number = Math.random,
): number {
  try {
    const stored = storage?.getItem(BUCKET_STORAGE_KEY);
    if (stored !== null && stored !== undefined && stored !== '') {
      const bucket = Number(stored);
      if (Number.isInteger(bucket) && bucket >= 0 && bucket < BUCKET_COUNT) return bucket;
    }
  } catch {
    // stockage indisponible : nouveau tirage, sans conséquence au-delà de la visite
  }
  const bucket = Math.floor(random() * BUCKET_COUNT);
  try {
    storage?.setItem(BUCKET_STORAGE_KEY, String(bucket));
  } catch {
    // idem
  }
  return bucket;
}

/** Le tirage n'est lu (et stocké) que si le canary est ouvert : sans config, rien ne change. */
export function shouldUseLiveApi(
  liveApiUrl: string | undefined,
  canaryPercent: number,
  readBucket: () => number,
): boolean {
  if (!liveApiUrl || canaryPercent <= 0) return false;
  return readBucket() < canaryPercent;
}

/**
 * Seuls les échecs d'infrastructure repassent par welya-api. Les 400/401/404 et les 403 portant un
 * code applicatif sont des réponses métier identiques sur les deux services : on les rend telles
 * quelles. Un 403 sans code vient de Cloud Armor ; 424 signale un état live indisponible côté live-api.
 */
export function isLiveApiFallbackError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return true;
  const { status, code, message } = error as { status?: number; code?: string; message?: string };
  if (code === 'NETWORK' || status === undefined) return true;
  if (status === 403) return !(typeof message === 'string' && message.startsWith('HTTP.'));
  return status === 424 || status === 429 || status >= 500;
}

const LIVE_API_REQUIRED_CLAIMS = [
  'attendeeId',
  'email',
  'sessionId',
  'sessionIdentifier',
  'webinarId',
  'workspaceId',
  'subscriptionId',
  'jti',
] as const;

/**
 * live-api n'accepte que les tokens d'inscription portant tous leurs claims : invitations, équipe et
 * anciens tokens incomplets restent sur welya-api, qui sait les résoudre.
 */
export function isLiveApiEligibleAuthorization(authorization: string | undefined): boolean {
  if (!authorization) return false;
  const token = authorization.replace(/^Bearer\s+/i, '').trim();
  try {
    const claims = jwtDecode<Record<string, unknown>>(token);
    if (String(claims.type ?? '').toUpperCase() !== 'SUBSCRIPTION') return false;
    return LIVE_API_REQUIRED_CLAIMS.every((claim) => {
      const value = claims[claim];
      return (
        (typeof value === 'number' && Number.isFinite(value)) ||
        (typeof value === 'string' && value.trim() !== '')
      );
    });
  } catch {
    return false;
  }
}
